import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Bell, Send, Users, Smartphone, Monitor, 
  TrendingUp, Clock, RefreshCcw, Image as ImageIcon,
  CheckCircle, XCircle, AlertTriangle, Upload, X
} from 'lucide-react';
import api from '../../services/api';
import { BackButton, PageSkeleton } from '../../components/common';

const AdminNotificationCenterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const fileInputRef = useRef(null);
  
  // Broadcast form
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('');
  const [broadcastImage, setBroadcastImage] = useState(null);
  const [broadcastImagePreview, setBroadcastImagePreview] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/notifications/stats');
      setStats(response.data);
      setLogs(response.data.recent_logs || []);
    } catch (error) {
      toast.error('Failed to load notification stats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      
      setBroadcastImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBroadcastImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setBroadcastImage(null);
    setBroadcastImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    
    if (!broadcastTitle.trim() || !broadcastBody.trim()) {
      toast.error('Please enter both title and body');
      return;
    }
    
    try {
      setSending(true);
      
      const payload = {
        title: broadcastTitle,
        body: broadcastBody,
      };
      
      // Include image data if uploaded
      if (broadcastImagePreview) {
        payload.image_data = broadcastImagePreview;
      }
      
      const response = await api.post('/admin/notifications/broadcast', payload);
      
      toast.success(response.data.message || 'Broadcast sent successfully');
      
      // Clear form
      setBroadcastTitle('');
      setBroadcastBody('');
      handleRemoveImage();
      
      // Refresh stats
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleTriggerRateAlert = async () => {
    try {
      setSending(true);
      const response = await api.post('/admin/notifications/send-rate-alert');
      toast.success(response.data.message || 'Rate alert sent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send rate alert');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationTypeColor = (type) => {
    switch (type) {
      case 'daily_snapshot': return 'bg-blue-100 text-blue-800';
      case 'gold_rate_alert': return 'bg-yellow-100 text-yellow-800';
      case 'admin_broadcast': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackButton to="/admin" className="mr-2" />
          <Bell className="h-8 w-8 text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">Notification Center</h1>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCcw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-xs text-gray-400">All registered users</p>
              </div>
              <Users className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Push Enabled</p>
                <p className="text-2xl font-bold text-green-800">{stats?.registered_users || 0}</p>
                <p className="text-xs text-green-600">Can receive notifications</p>
              </div>
              <Bell className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Android</p>
                <p className="text-2xl font-bold">{stats?.platform_breakdown?.android || 0}</p>
              </div>
              <Smartphone className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">iOS</p>
                <p className="text-2xl font-bold">{stats?.platform_breakdown?.ios || 0}</p>
              </div>
              <Smartphone className="h-8 w-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Web</p>
                <p className="text-2xl font-bold">{stats?.platform_breakdown?.web || 0}</p>
              </div>
              <Monitor className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sent Today</p>
                <p className="text-2xl font-bold">{stats?.notifications_today || 0}</p>
              </div>
              <Bell className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Note about unregistered users */}
      {stats?.unregistered_users > 0 && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-900">
                  {stats.unregistered_users} users cannot receive push notifications
                </p>
                <p className="text-sm text-yellow-800 mt-1">
                  These users haven't enabled notifications on their device yet. Push notifications require:
                </p>
                <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                  <li><strong>Mobile App:</strong> User must open the app and grant notification permission</li>
                  <li><strong>Web App:</strong> User must click "Enable Notifications" banner and allow in browser</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Broadcast Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Broadcast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendBroadcast} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <Input
                  value={broadcastTitle}
                  onChange={(e) => setBroadcastTitle(e.target.value)}
                  placeholder="Notification title..."
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body *
                </label>
                <Textarea
                  value={broadcastBody}
                  onChange={(e) => setBroadcastBody(e.target.value)}
                  placeholder="Notification message..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image (optional)
                </label>
                
                {broadcastImagePreview ? (
                  <div className="relative inline-block">
                    <img 
                      src={broadcastImagePreview} 
                      alt="Preview" 
                      className="w-32 h-32 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-amber-400 transition-colors"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to upload image</p>
                    <p className="text-xs text-gray-400 mt-1">Max 5MB, JPG/PNG</p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              <Button 
                type="submit" 
                disabled={sending || !broadcastTitle.trim() || !broadcastBody.trim()}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {sending ? (
                  <>
                    <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to All Users
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 pt-4 border-t">
              <Button 
                onClick={handleTriggerRateAlert}
                disabled={sending}
                variant="outline"
                className="w-full"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Send Rate Alert Now
              </Button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Triggers an immediate gold rate notification to all subscribed users
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Recent Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No notifications sent yet</p>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getNotificationTypeColor(log.type)}>
                            {log.type?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatDate(log.sent_at)}
                          </span>
                        </div>
                        <p className="font-medium text-sm">{log.title}</p>
                        <p className="text-xs text-gray-600 line-clamp-2">{log.body}</p>
                      </div>
                      <div className="text-right ml-2">
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3 w-3" />
                          <span className="text-xs">{log.success_count}</span>
                        </div>
                        {log.failure_count > 0 && (
                          <div className="flex items-center gap-1 text-red-600">
                            <XCircle className="h-3 w-3" />
                            <span className="text-xs">{log.failure_count}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Notification Schedule</p>
              <ul className="text-sm text-blue-800 mt-1 space-y-1">
                <li>• <strong>Daily Snapshot:</strong> Sent automatically at 10:30 AM IST</li>
                <li>• <strong>Intra-day Alerts:</strong> Sent when gold rate changes from day's opening</li>
                <li>• <strong>Max Limit:</strong> Users receive maximum 5 notifications per day</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNotificationCenterPage;
